import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Button from '../../../components/atoms/forms/Button';
import { stopDisplayState } from '../../../utils/composeDriverRecords';
import {
  STOP_STATE_LABEL,
  STOP_STATE_TONE,
  discClassVariants,
  toneClassVariants,
} from './statusStyles';
import { formatOnSiteDuration, pluralize } from '../../../utils/displayFormat';

// The driver day panel: what this person did today, in the order they did
// it, with the route each stop belonged to. The summary stays pinned above
// the list, which scrolls inside the panel so a long day never runs off the
// bottom of the card. Discs are the same ones the map draws for these stops.

const StyledDiv = styledComponent('div', {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,

  '& .dds-summary': {
    padding: `0 ${theme.spacing.gapMd} ${theme.spacing.gapXl}`,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    flex: '0 0 auto',
  },
  '& .dds-total': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textBody,
  },
  '& .dds-routes': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.gapXs,
    marginTop: theme.spacing.gapMd,
  },
  '& .dds-route-row': {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing.gapMd,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
  },
  '& .dds-list': {
    listStyle: 'none',
    margin: 0,
    padding: `0 ${theme.spacing.gapMd}`,
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  '& .dds-row': {
    display: 'grid',
    gridTemplateColumns: `${theme.sizes.stopBadgeSize} 1fr auto`,
    columnGap: theme.spacing.gapLg,
    alignItems: 'start',
    padding: `${theme.spacing.gapLg} 0`,
  },
  '& .dds-row + .dds-row': {
    borderTop: `1px solid ${theme.colors.borderLight}`,
  },
  '& .dds-disc': {
    width: theme.sizes.stopBadgeSize,
    height: theme.sizes.stopBadgeSize,
    borderRadius: theme.radii.circle,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    ...theme.typography.badge,
    fontWeight: theme.fontWeights.semibold,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  ...discClassVariants('dds-disc'),
  '& .dds-stop-name': {
    fontWeight: theme.fontWeights.semibold,
    overflowWrap: 'anywhere',
  },
  '& .dds-lines': {
    marginTop: theme.spacing.gapXs,
    display: 'flex',
    flexDirection: 'column',
    rowGap: theme.spacing.gapXs,
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textBody,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .dds-line-muted': {
    color: theme.colors.textMuted,
  },
  '& .dds-chip': {
    padding: theme.spacing.pillPadding,
    borderRadius: theme.radii.pill,
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.medium,
    whiteSpace: 'nowrap',
  },
  ...toneClassVariants('dds-chip', STOP_STATE_TONE),
});

function DriverDayStops({ day, onOpenRoute }) {
  return (
    <StyledDiv>
      <div className="dds-summary">
        <div className="dds-total">
          {`${pluralize(day.stopsServiced, 'stop')} serviced across ${pluralize(
            day.routes.length,
            'route',
          )}`}
        </div>
        <div className="dds-routes">
          {day.routes.map((route) => (
            <div className="dds-route-row" key={route.routeId}>
              <Button
                variant="link-primary"
                title="Open this route"
                onClick={() => onOpenRoute(route.routeId)}
              >
                {route.routeName}
              </Button>
              <span>{pluralize(route.stopsHere, 'stop')}</span>
            </div>
          ))}
        </div>
      </div>
      <ol className="dds-list">
        {day.stops.map((stop, index) => {
          const displayState = stopDisplayState(stop);
          const hasDuration =
            stop.actualDurationMin !== null &&
            stop.actualDurationMin !== undefined;
          return (
            <li key={`${stop.routeId}-${stop.stopId}`} className="dds-row">
              <span className={`dds-disc ${displayState}`}>{index + 1}</span>
              <div>
                <div className="dds-stop-name">
                  <Button
                    variant="link-primary"
                    title={`Show ${
                      stop.stopName || stop.stopId
                    } on its route map`}
                    onClick={() => onOpenRoute(stop.routeId, stop.stopId)}
                  >
                    {stop.stopId}
                  </Button>
                </div>
                <div className="dds-lines">
                  {stop.scannedAt ? (
                    <span>{`Scanned ${stop.scannedAt}`}</span>
                  ) : null}
                  {stop.stockedAt ? (
                    <span>{`Stocked ${stop.stockedAt}`}</span>
                  ) : null}
                  {hasDuration ? (
                    <span className="dds-line-muted">
                      {formatOnSiteDuration(stop.actualDurationMin, stop.type)}
                    </span>
                  ) : null}
                  <span className="dds-line-muted">{stop.routeName}</span>
                </div>
              </div>
              <span className={`dds-chip ${displayState}`}>
                {STOP_STATE_LABEL[displayState]}
              </span>
            </li>
          );
        })}
      </ol>
    </StyledDiv>
  );
}

export const driverDayShape = PropTypes.shape({
  email: PropTypes.string.isRequired,
  name: PropTypes.string,
  stops: PropTypes.arrayOf(PropTypes.object).isRequired,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      routeId: PropTypes.string.isRequired,
      routeName: PropTypes.string,
      status: PropTypes.string,
      stopsHere: PropTypes.number,
    }),
  ).isRequired,
  stopsServiced: PropTypes.number.isRequired,
  currentStop: PropTypes.object,
});

DriverDayStops.propTypes = {
  day: driverDayShape.isRequired,
  // (routeId, stopId?) — open that route's map, focused on the stop if given.
  onOpenRoute: PropTypes.func.isRequired,
};

export default DriverDayStops;
